import React, { useEffect, useState } from 'react';
import './statementHistoryDownload.css';
import { ButtonHeading } from 'components';
import { AiOutlineMail } from 'react-icons/ai';
import { LiaDownloadSolid } from 'react-icons/lia';
import { StorageServices } from 'utils';
import { getPaynowDownloadApi } from '_services/accountingServices/accountingServices';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .toUpperCase(); // e.g. "05 DEC 2025"
};

const formatRequestedOn = (isoString) => {
  const date = new Date(isoString);
  return date
    .toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toUpperCase(); // e.g. "18 FEB 2026, 07:17 AM"
};

const mapApiData = (data = []) =>
  data.map((item) => ({
    id: item._id,
    dateRange: `${formatDate(item.start_date)} - ${formatDate(item.end_date)}`,
    type: 'Bank Statement',
    format: item.format,
    fileSize: item.file_size_mb,
    requestedOn: formatRequestedOn(item.updatedAt),
    status: item.status === 1 ? 'Successful' : 'Failed',
    doc_url: item.doc_url,
  }));

// ─── Component ───────────────────────────────────────────────────────────────

const StatementHistoryDownload = ({ statements }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadFilesList, setDownloadFilesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const firmID = StorageServices?.getFirmId();

  // ── API Call ────────────────────────────────────────────────────────────────

  const getPaynowDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPaynowDownloadApi({ firm_id: firmID });
      if (res?.responseCode == 200) {
        setDownloadFilesList(mapApiData(res?.data?.statement_data));
      } else {
        setError('Failed to fetch statements. Please try again.');
      }
    } catch (err) {
      console.error('Failed to fetch downloads:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (firmID) {
      getPaynowDownload();
    }
  }, [firmID]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleDownloadClick = (statement) => {
    if (statement.doc_url) {
      window.open(statement.doc_url, '_blank');
    }
  };

  const handleEmailClick = (statement) => {
    console.log('Email statement:', statement.doc_url);
    // Add your email API logic here
  };

  // ── Filter ───────────────────────────────────────────────────────────────────

  const filteredStatements = downloadFilesList.filter(
    (statement) =>
      statement.dateRange.toLowerCase().includes(searchTerm.toLowerCase()) ||
      statement.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      statement.format.toLowerCase().includes(searchTerm.toLowerCase()) ||
      statement.requestedOn.toLowerCase().includes(searchTerm.toLowerCase()) ||
      statement.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <ButtonHeading title="Downloads" buttonTitle="Add Account" />

      <div className="__accountMain_bxs sh-Main">
        <div className="__connectDetail_otr">
          <div className="container-fluid">
            <div className="sh-table-container sh-sub-main">

              {/* Info Banner */}
              

              {/* Loading State */}
              {loading && (
                <div className="sh-empty-state">
                  <p>Loading statements...</p>
                </div>
              )}

              {/* Error State */}
              {!loading && error && (
                <div className="sh-empty-state">
                  <p>{error}</p>
                  <button className="sh-btn-download" onClick={getPaynowDownload}>
                    Retry
                  </button>
                </div>
              )}

              {/* Table */}
              {!loading && !error && (
                <div className="sh-table-wrapper">
                  {filteredStatements.length > 0 ? (
                    filteredStatements.map((statement) => (
                      <div key={statement.id} className="sh-card">
                        <div className="sh-card-content">

                          <div className="sh-card-item">
                            <div className="sh-cell-main">{statement.dateRange}</div>
                            <div className="sh-cell-sub">Date Range</div>
                          </div>

                          <div className="sh-card-item">
                            <div className="sh-cell-main">{statement.type}</div>
                            <div className="sh-cell-sub">Type</div>
                          </div>

                          <div className="sh-card-item">
                            <div className="sh-cell-main">{statement.format}</div>
                            <div className="sh-cell-sub">Format</div>
                          </div>

                          <div className="sh-card-item">
                            <div className="sh-cell-main">{statement.fileSize}</div>
                            <div className="sh-cell-sub">File Size</div>
                          </div>

                          <div className="sh-card-item">
                            <div className="sh-cell-main">{statement.requestedOn}</div>
                            <div className="sh-cell-sub">Requested on</div>
                          </div>

                          <div className="sh-card-item">
                            <div
                              className={`sh-cell-main ${
                                statement.status === 'Successful'
                                  ? 'sh-status-success'
                                  : 'sh-status-failed'
                              }`}
                            >
                              {statement.status}
                            </div>
                            <div className="sh-cell-sub">Status</div>
                          </div>

                          <div className="sh-card-item sh-card-actions">
                            <div className="sh-action-buttons">
                              
                              <button
                                className="sh-btn-download"
                                onClick={() => handleDownloadClick(statement)}
                              >
                                Download <LiaDownloadSolid />
                              </button>
                            </div>
                          </div>

                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="sh-empty-state">
                      <p>No statements found</p>
                      <small>Check back later for your download history</small>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatementHistoryDownload;